import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { combineLatest, filter, take } from 'rxjs';
import { PosthogService } from '@/app/core/telemetry/posthog.service';

/**
 * Fail-closed auth bootstrap. The router has an Auth0 guard, but this startup
 * check makes the whole SPA redirect as soon as Auth0 finishes session restore
 * and reports that the user is not authenticated.
 */
@Injectable({ providedIn: 'root' })
export class AuthRedirectService {
  private readonly auth = inject(AuthService);
  private readonly document = inject(DOCUMENT);
  private readonly posthog = inject(PosthogService);
  private redirectStarted = false;

  initialize(): void {
    combineLatest([this.auth.isLoading$, this.auth.isAuthenticated$])
      .pipe(
        filter(([loading]) => !loading),
        take(1)
      )
      .subscribe(([, isAuthenticated]) => {
        if (!isAuthenticated) {
          this.redirectToLogin();
        }
      });
  }

  /** Redirect to Auth0 while preserving the current application target. */
  redirectToLogin(): void {
    if (this.redirectStarted) {
      return;
    }

    this.redirectStarted = true;
    this.auth
      .loginWithRedirect({
        appState: {
          target: this.currentTargetPath(),
        },
      })
      .subscribe({
        error: (error: unknown) => {
          // A failed login redirect strands the user on a blank shell —
          // record it so silent auth breakage is visible in telemetry.
          this.posthog.captureException(error, {
            source: 'AuthRedirectService.redirectToLogin',
          });
          this.redirectStarted = false;
        },
      });
  }

  private currentTargetPath(): string {
    const { hash, pathname, search } = this.document.location;
    return `${pathname || '/'}${search}${hash}`;
  }
}
